var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
import Button from '@doordash/component-button';
import { Modal } from '@doordash/component-modal';
import { Icon, Inset } from '@doordash/design-language';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { useDebounce } from 'use-debounce';
import { INPUT_DEBOUNCE_DELAY, BACKEND_SEARCH_EMAIL_MIN_LENGTH, } from '../../../constants/ui';
import { MembersActions } from '../../../constants/actions';
import { FetchStatus } from '../../../constants/fetch_status';
import { searchMembers } from '../../members_slice';
import { addTeamAccountGroupMember } from '../groups_members_slice';
import { getSelectedTeamAccount } from '../../../selectors/uiSelector';
import { getStatusForAction as getStatusForMemberActions } from '../../../selectors/members_selector';
import { SearchableOptionsSelect } from '../../searchable_option_select';
export var getEmailSelectorOptions = function (teamAccountMembers) {
    var emailsOptionsFromTeamMembers = teamAccountMembers.map(function (teamMember) {
        var id = teamMember.id;
        return __assign(__assign({}, teamMember), { text: teamMember.email, value: id });
    });
    return emailsOptionsFromTeamMembers;
};
export var getAddGroupMembersSuccessMsg = function (group, options) {
    var memberCount = options.memberCount;
    var membersString = options.memberCount > 1 ? 'members' : 'member';
    var groupName = (group === null || group === void 0 ? void 0 : group.name) || '';
    return memberCount + " " + membersString + " added to the `" + groupName + "` group successfully";
};
export var displayAddGroupMembersSuccessToast = function (group, options, displayToast) {
    displayToast({
        icon: Icon.Types.Check,
        text: getAddGroupMembersSuccessMsg(group, options),
        insetFromEdge: Inset.Sizes.XxxLarge,
    });
};
var AddMembersToGroupModalContent = function (_a) {
    var inputAnchorName = _a.inputAnchorName, searchingMemberStatus = _a.searchingMemberStatus, dispatch = _a.dispatch, selectedTeamAccount = _a.selectedTeamAccount, selectedOptions = _a.selectedOptions, setSelectedOptions = _a.setSelectedOptions;
    var _b = __read(React.useState(false), 2), isLoading = _b[0], setIsLoading = _b[1];
    var _c = __read(React.useState(false), 2), searchError = _c[0], setSearchError = _c[1];
    React.useEffect(function () {
        if (searchingMemberStatus === FetchStatus.PENDING) {
            setIsLoading(true);
            setSearchError(false);
        }
        else if (searchingMemberStatus === FetchStatus.REJECTED) {
            setIsLoading(false);
            setSearchError(true);
        }
        else if (searchingMemberStatus === FetchStatus.FULFILLED ||
            searchingMemberStatus === FetchStatus.INVALID_STATUS) {
            setIsLoading(false);
            setSearchError(false);
        }
    }, [searchingMemberStatus, setIsLoading]);
    var _d = __read(React.useState(''), 2), searchText = _d[0], setSearchText = _d[1];
    var _e = __read(useDebounce(searchText, INPUT_DEBOUNCE_DELAY), 2), debouncedSearchText = _e[0], isPending = _e[1].isPending;
    var _f = __read(React.useState([]), 2), emailOptions = _f[0], setEmailOptions = _f[1];
    var clearSearchText = function () { return setSearchText(''); };
    var onAddOption = React.useCallback(function (option) {
        setSelectedOptions(__spread(selectedOptions, [option]));
    }, [setSelectedOptions, selectedOptions]);
    var onRemoveOption = React.useCallback(function (option) {
        var newSelectedOptions = __spread(selectedOptions).filter(function (opt) {
            return opt.value !== option.value;
        });
        setSelectedOptions(newSelectedOptions);
    }, [setSelectedOptions, selectedOptions]);
    React.useEffect(function () {
        if (!debouncedSearchText ||
            debouncedSearchText.length < BACKEND_SEARCH_EMAIL_MIN_LENGTH) {
            setEmailOptions([]);
            return;
        }
        dispatch(searchMembers({
            teamAccountId: selectedTeamAccount,
            pageNumber: '1',
            queryString: debouncedSearchText,
        }))
            .unwrap()
            .then(function (teamMembers) {
            var emailsOptionsFromTeamMembers = getEmailSelectorOptions(teamMembers);
            setEmailOptions(emailsOptionsFromTeamMembers);
        });
    }, [debouncedSearchText, dispatch, selectedTeamAccount]);
    return (React.createElement(SearchableOptionsSelect, { width: '528px', placeholder: "Search by employee email", noOptionText: isPending() ||
            (debouncedSearchText || '').length < BACKEND_SEARCH_EMAIL_MIN_LENGTH
            ? ''
            : 'No email matching your search', name: inputAnchorName, clearSearchText: clearSearchText, setSearchText: setSearchText, options: emailOptions, selectedOptions: selectedOptions, searchText: searchText, onAddOption: onAddOption, onRemoveOption: onRemoveOption, label: "", errorMessage: searchError
            ? 'Something went wrong searching for members. Please try again'
            : undefined, isLoading: isLoading, allowMultiSelect: false }));
};
export var AddMembersToGroupModal = function (_a) {
    var _b, _c;
    var group = _a.group, onClose = _a.onClose, onSuccess = _a.onSuccess;
    var isVisible = !!group;
    var _d = group || {}, _e = _d.id, groupId = _e === void 0 ? '' : _e, _f = _d.name, groupName = _f === void 0 ? '' : _f;
    var dispatch = useDispatch();
    var selectedTeamAccount = useSelector(getSelectedTeamAccount);
    var _g = __read(React.useState(false), 2), isSubmitting = _g[0], setIsSubmitting = _g[1];
    var _h = __read(React.useState([]), 2), selectedOptions = _h[0], setSelectedOptions = _h[1];
    var cleanUpAndClose = function () {
        setIsSubmitting(false);
        setSelectedOptions([]);
        onClose();
    };
    var primaryButtonProps = {
        isDisabled: selectedOptions.length === 0,
        state: isSubmitting ? Button.States.Loading : Button.States.Default,
    };
    var addMembersToGroup = function () {
        var selectedMemberIds = selectedOptions.map(function (option) {
            return option.value;
        });
        setIsSubmitting(true);
        dispatch(addTeamAccountGroupMember({
            groupId: groupId,
            memberIds: selectedMemberIds,
        }))
            .then(unwrapResult)
            .then(function (_a) {
            var success = _a.success;
            if (success) {
                onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(group, { memberCount: selectedMemberIds.length || 0 });
                cleanUpAndClose();
            }
        })
            .then(function () {
            setIsSubmitting(false);
        });
    };
    var statusForMemberActions = useSelector(getStatusForMemberActions);
    var searchingMemberStatus = !!statusForMemberActions && ((_c = (_b = statusForMemberActions) === null || _b === void 0 ? void 0 : _b[MembersActions.SEARCH_MEMBERS]) === null || _c === void 0 ? void 0 : _c.status);
    if (!isVisible) {
        return null;
    }
    return (React.createElement(Modal, { onClose: cleanUpAndClose, title: "Add Members to " + groupName, primaryAction: {
            text: 'Add',
            onClick: function () { return addMembersToGroup(); },
            buttonProps: primaryButtonProps,
        }, secondaryAction: {
            text: 'Cancel',
            onClick: function () { return cleanUpAndClose(); },
        } },
        React.createElement(AddMembersToGroupModalContent, { inputAnchorName: groupId, selectedOptions: selectedOptions, setSelectedOptions: setSelectedOptions, selectedTeamAccount: selectedTeamAccount, dispatch: dispatch, searchingMemberStatus: searchingMemberStatus })));
};
export default AddMembersToGroupModal;
